import type { StressTestFocusId } from './guestSession';
import {
  classifyMentorReality,
  enforceAntiParroting,
} from './reflectiveDialogue';

export function trimTo45SecondRead(text: string): string {
  const words = text.trim().replace(/\s+/g, ' ').split(' ');
  const maxWords = 75;
  if (words.length <= maxWords) return words.join(' ');
  return `${words.slice(0, maxWords).join(' ')}...`;
}

function enforceAdviceBan(response: string): string {
  const bannedPatterns = [
    /you should/i,
    /you must/i,
    /here's how to/i,
    /step 1:/i,
    /the solution is/i,
    /i recommend/i,
    /according to/i,
    /research shows/i,
    /the theory behind/i,
    /let me educate/i,
  ];
  let content = response;
  for (const pattern of bannedPatterns) {
    if (pattern.test(content)) {
      content = trimTo45SecondRead(
        'Got it — let me put that plainly. What\'s one thing in your control you could test this month, and how would you know it helped?',
      );
      break;
    }
  }
  return content;
}

const FOCUS_LABELS: Record<StressTestFocusId, string> = {
  pricing: 'Pricing / Margin',
  scaling: 'Talent / Scaling',
  capital: 'Capital / Product',
};

const FOCUS_CLAIM_AUDITS: Record<StressTestFocusId, string> = {
  pricing: `
    Good — price and margin. Most owners feel this in the gut before the spreadsheet catches up.
    What's the quiet assumption you've been sitting on: the price is about right, thin margins are "just the market,"
    or customers will wear the next increase without walking? Which of those would sting most if it was wrong?
  `,
  scaling: `
    Bringing on new people usually sounds like the answer, but it can double your headache if the baseline isn't solid.
    What's the hope underneath — that hiring will fix the bottleneck, that you can lead the same way with a bigger crew,
    or that culture will hold as you grow? Which of those feels shakiest when you're honest with yourself?
  `,
  capital: `
    Cash and what you're building — that's the adult conversation.
    What are you banking on that you haven't really tested yet: the product is close enough, the cash will last,
    or the next feature will unlock demand? Which would hurt most if it didn't hold over the next few months?
  `,
};

const FOCUS_EVIDENCE_PROBES: Record<StressTestFocusId, string> = {
  pricing: `
    Which signal is least reliable at present: the true retained margin, customer tolerance,
    or the cost that appears only after delivery begins?
  `,
  scaling: `
    If demand increased next week, where would capacity fail first: training, supervision,
    or the day-to-day work already resting with the current team?
  `,
  capital: `
    Which evidence would change the decision most: the real cash runway, dependable demand,
    or proof that the product is creating value rather than activity?
  `,
};

const FOCUS_BLIND_SPOTS: Record<StressTestFocusId, [string, string]> = {
  pricing: [
    'Margin looks fine on paper while discounts and extras quietly eat what each job actually leaves you.',
    'Price rises are planned without a clear line for how many customers walking away is too many.',
  ],
  scaling: [
    'Hiring is carrying the growth plan while who has time to train new people stays fuzzy.',
    'What worked with a smaller crew is assumed to hold when workload doubles — that is usually where weekends disappear.',
  ],
  capital: [
    'Cash is treated as if it will last while product cycles stretch and proof of traction stays soft.',
    'Money is going into building more features instead of into sales, retention, or cash coming back in.',
  ],
};

function resolveFocusArea(
  focusArea: StressTestFocusId | null | undefined,
  content: string,
): StressTestFocusId {
  if (focusArea && FOCUS_LABELS[focusArea]) return focusArea;

  const lower = content.toLowerCase();
  if (/pricing|margin|price|discount|churn/.test(lower)) return 'pricing';
  if (/talent|scaling|hire|headcount|staff|team/.test(lower)) return 'scaling';
  if (/capital|product|runway|burn|funding|feature/.test(lower)) return 'capital';
  return 'pricing';
}

export function countUserTurns(messages: Array<{ role: string }> = []): number {
  return messages.filter((m) => m.role === 'user').length;
}

/** Turn-1 scripts only — Turns 2–3 reflect live user meaning and are synthesized on demand. */
export function getAllStressTestScripts(): Array<{
  focusArea: StressTestFocusId;
  turnNumber: number;
  content: string;
}> {
  const focuses: StressTestFocusId[] = ['pricing', 'scaling', 'capital'];
  return focuses.map((focusArea) => ({
    focusArea,
    turnNumber: 1,
    content: buildGuestStressTestResponse({
      turnNumber: 1,
      content: `Focus area: ${FOCUS_LABELS[focusArea]}`,
      focusArea,
    }).content,
  }));
}

export function stressTestScriptCacheKey(focusArea: string, turnNumber: number): string {
  return `${focusArea}:${turnNumber}`;
}

export function buildGuestStressTestResponse({
  turnNumber,
  content,
  focusArea,
  priorUserContent,
}: {
  turnNumber: number;
  content: string;
  focusArea?: StressTestFocusId | null;
  priorUserContent?: string | null;
}) {
  const focus = resolveFocusArea(focusArea, content);
  const finish = (text: string, source = content) => {
    const reality = classifyMentorReality(source);
    const fallback = trimTo45SecondRead(
      `${reality.reframe} ${reality.hold} ${reality.invitation}`,
    );
    const safe = enforceAdviceBan(trimTo45SecondRead(text));
    return enforceAntiParroting(source, safe, fallback);
  };

  if (turnNumber === 1) {
    return {
      content: finish(FOCUS_CLAIM_AUDITS[focus]),
      focusArea: focus,
      turnNumber: 1,
      isLocked: false,
    };
  }

  if (turnNumber === 2) {
    const reality = classifyMentorReality(content);
    const closing =
      reality.gravity === 'PROFOUND'
        ? reality.invitation
        : FOCUS_EVIDENCE_PROBES[focus];
    return {
      content: finish(`${reality.reframe} ${reality.hold} ${closing}`),
      focusArea: focus,
      turnNumber: 2,
      isLocked: false,
    };
  }

  const turnTwoReality = classifyMentorReality(priorUserContent || content);
  const latestReality = classifyMentorReality(content);
  const spots =
    turnTwoReality.id !== 'operational_uncertainty'
      ? turnTwoReality.summary
      : latestReality.id !== 'operational_uncertainty'
        ? latestReality.summary
        : FOCUS_BLIND_SPOTS[focus];
  const gravityIsProfound =
    turnTwoReality.gravity === 'PROFOUND' || latestReality.gravity === 'PROFOUND';
  const closing = gravityIsProfound
    ? 'The strategic work can wait until there is enough steadiness to approach it without adding pressure.'
    : 'Two practical risks showed up in this short session.';
  const summary = `
    ${turnTwoReality.validation}
    Here is the two-point strategic summary on ${FOCUS_LABELS[focus]}:
    1. ${spots[0]}
    2. ${spots[1]}
    ${closing}
    To get the full Action Learning Plan and keep this conversation in your private vault, initialize your workspace.
  `;

  return {
    content: finish(summary, priorUserContent || content),
    focusArea: focus,
    turnNumber: 3,
    isLocked: true,
    blindSpotCount: 2,
  };
}
